import { useEffect, useMemo, useState } from 'react';
import type { RdwCheckResult, RdwCheckRow, RdwCheckVeld } from '@fleet/shared';
import { euro, fmtDate } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { Modal } from '../../components/ui/Modal';
import { ApiError } from '../../lib/apiClient';
import { useApplyRdwChanges } from './rdw.api';

const VELD_LABEL: Record<RdwCheckVeld, string> = {
  apkVervaldatum: 'APK-datum',
  catalogusprijs: 'Cat.waarde',
  merk: 'Merk',
  model: 'Model',
  brandstof: 'Brandstof',
};

function toon(veld: RdwCheckVeld, waarde: string | null): string {
  if (waarde == null || waarde === '') return '—';
  if (veld === 'apkVervaldatum') return fmtDate(waarde);
  if (veld === 'catalogusprijs') return euro(Number(waarde));
  return waarde;
}

export function RdwCheckModal({
  open,
  onClose,
  result,
  loading,
  checkError,
}: {
  open: boolean;
  onClose: () => void;
  result: RdwCheckResult | null;
  loading: boolean;
  checkError?: string;
}) {
  const apply = useApplyRdwChanges();
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const gewijzigd = useMemo(
    () => result?.rows.filter((r) => r.status === 'gewijzigd') ?? [],
    [result],
  );
  const problemen = result?.rows.filter((r) => r.status !== 'gewijzigd') ?? [];

  // Pre-select every changed car whenever a fresh result arrives.
  useEffect(() => {
    setSelected(new Set(gewijzigd.map((r) => r.kenteken)));
  }, [gewijzigd]);

  function toggle(kenteken: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kenteken)) next.delete(kenteken);
      else next.add(kenteken);
      return next;
    });
  }

  async function doApply() {
    setError('');
    try {
      await apply.mutateAsync(gewijzigd.filter((r) => selected.has(r.kenteken)));
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Bijwerken mislukt');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="RDW-check">
      {loading ? (
        <div style={{ padding: '20px 0', color: '#666666', fontSize: 14 }}>
          Alle auto&apos;s worden gecontroleerd bij de RDW…
        </div>
      ) : !result ? (
        <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ color: '#B0543B', fontSize: 14, fontWeight: 700 }}>
            {checkError || 'De RDW-check kon niet worden uitgevoerd.'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" type="button" onClick={onClose}>
              Sluiten
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
            <Badge tone="neutral">{result.gecontroleerd} gecontroleerd</Badge>
            <Badge tone="success">{result.inSync} in sync</Badge>
            {result.gewijzigd > 0 && <Badge tone="blue">{result.gewijzigd} gewijzigd</Badge>}
            {result.problemen > 0 && <Badge tone="warning">{result.problemen} probleem</Badge>}
          </div>

          {result.gewijzigd === 0 && result.problemen === 0 && (
            <div style={{ fontSize: 14, color: '#2A7050', fontWeight: 700 }}>
              Alles is up-to-date met de RDW. 🎉
            </div>
          )}

          {gewijzigd.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#666666',
                }}
              >
                Afwijkingen
              </div>
              {gewijzigd.map((row: RdwCheckRow) => (
                <label
                  key={row.kenteken}
                  style={{
                    display: 'flex',
                    gap: 10,
                    borderTop: '1px solid #F0E8DC',
                    paddingTop: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(row.kenteken)}
                    onChange={() => toggle(row.kenteken)}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, fontFamily: "'Roboto Mono', monospace" }}>
                        {row.kenteken}
                      </span>
                      <span style={{ color: '#666666' }}>{row.label}</span>
                    </div>
                    {row.changes.map((c) => (
                      <div key={c.veld} style={{ fontSize: 13, color: '#444444', marginTop: 2 }}>
                        {VELD_LABEL[c.veld]}:{' '}
                        <span style={{ color: '#B0543B' }}>{toon(c.veld, c.opgeslagen)}</span>
                        {' → '}
                        <span style={{ color: '#2A7050', fontWeight: 700 }}>
                          {toon(c.veld, c.rdw)}
                        </span>
                      </div>
                    ))}
                  </div>
                </label>
              ))}
            </div>
          )}

          {problemen.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#666666',
                }}
              >
                Niet te controleren
              </div>
              {problemen.map((row) => (
                <div
                  key={row.kenteken}
                  style={{
                    fontSize: 13,
                    color: '#444444',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontWeight: 700, fontFamily: "'Roboto Mono', monospace" }}>
                    {row.kenteken}
                  </span>
                  <span style={{ color: '#666666' }}>{row.label}</span>
                  <Badge tone="warning">
                    {row.status === 'niet_gevonden' ? 'Niet gevonden' : 'Fout'}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Button variant="ghost" type="button" onClick={onClose}>
              Sluiten
            </Button>
            {gewijzigd.length > 0 && (
              <Button
                variant="primary"
                type="button"
                onClick={doApply}
                disabled={apply.isPending || selected.size === 0}
              >
                {apply.isPending ? 'Bijwerken…' : `Bijwerken (${selected.size})`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
